import {isUndefined} from 'lodash';
import {createSelector, OutputSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {AppState} from '../../appState';
import {getHasPermission} from '../../data/access/accessSelectors';
import {getFlatTeams} from '../../data/teams/teamsSelectors';
import {getFlatPanels, getPanels} from '../panelsSelectors';
import {panelIsAnalyticsPanel, PanelState} from '../panelsState';
import {AnalyticsPanelId, PanelTypesEnum} from '../panelStateBase';
import {AnalyticsPanelState} from './analyticsPanelState';

/*
 * Constants.
 */

export const ANALYTICS_ROOM_NAME_PREFIX = 'analyticsreport';

/*
 * Panel selectors.
 */

// Get the first analytics panel, if any.
/** @deprecated Use makeGetAnalyticsPanel() to obtain a parameterized selector instead. */
export const getAnalyticsPanel: OutputSelector<
  AppState,
  AnalyticsPanelState,
  (panels: PanelState[]) => AnalyticsPanelState
> = createSelector(getFlatPanels, (panels) => {
  const panel = panels.find(panelIsAnalyticsPanel);
  if (!panel || panel.type !== PanelTypesEnum.ANALYTICS) {
    throw new Error('This analytics panel could not be found.');
  }

  return panel;
});

export interface AnalyticsPanelProps {
  panelId: AnalyticsPanelId;
}

export type AnalyticsPanelIdSelector<P> = AppParametricSelector<P, AnalyticsPanelId>;

function getAnalyticsPanelId(_state: unknown, props: AnalyticsPanelProps) {
  return props.panelId;
}

export type AnalyticsPanelSelector<P> = AppParametricSelector<P, AnalyticsPanelState>;

export function makeGetAnalyticsPanel(): AnalyticsPanelSelector<AnalyticsPanelProps>;
export function makeGetAnalyticsPanel<P>(getPanelId: AnalyticsPanelIdSelector<P>): AnalyticsPanelSelector<P>;
export function makeGetAnalyticsPanel(getPanelId: AnalyticsPanelIdSelector<any> = getAnalyticsPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.ANALYTICS) {
      throw new Error('Analytics panel not found.');
    }

    return panel;
  });
}

export function makeGetAnalyticsPath() {
  return createSelector(
    makeGetAnalyticsPanel(),
    ({parameters}) => `${parameters.location.pathname}${parameters.location.search}`,
  );
}

export const isAnalyticsLoaded = createSelector(
  getAnalyticsPanel,
  ({status}) => status === AsyncStatusesEnum.LOADED,
);

export const getUnsavedViewNavigationBlockerId = createSelector(
  getAnalyticsPanel,
  ({unsavedViewNavigationBlockerId}) => unsavedViewNavigationBlockerId,
);

// We cannot reuse getAnalyticsPanel as we don't want to throw an error if analytics panel is not found.
export const getAnalyticsPanelsRooms: OutputSelector<
  AppState,
  ReadonlyArray<string>,
  (panels: PanelState[]) => ReadonlyArray<string>
> = createSelector(getFlatPanels, (panels) => {
  const panel = panels.find(panelIsAnalyticsPanel);

  if (!panel || panel.type !== PanelTypesEnum.ANALYTICS) {
    return [];
  }

  const {
    report: {jobs},
  } = panel;

  return jobs
    .filter(({jobUid}) => !isUndefined(jobUid))
    .map(({jobUid}) => `${ANALYTICS_ROOM_NAME_PREFIX}-${jobUid}`);
});

// Get all teams where the user can access analytics.
export const getTeamsAccessibleForAnalytics = createSelector(
  [getFlatTeams, getHasPermission],
  (teams, hasPermission) =>
    teams.filter((team) => hasPermission(team.namespace, PermissionsEnum.READ_ANALYTICS)),
);
